import { Row } from 'components/v2/Row'
import { FieldArray, Formik, getIn, useFormikContext } from 'formik'
import React, { Fragment, ReactElement } from 'react'
import { useIntl, FormattedMessage, defineMessages } from 'react-intl'
import { NavLink } from 'react-router-dom'
import styled from 'styled-components'
import IconButton from 'components/v2/IconButton'
import { Button } from 'components/v2/Button'

import { Input } from 'components/Input'
import { ErrorMessage } from 'components/v2/ErrorMessage'
import { Column } from 'components/v2/Column'
import Heading from 'components/v2/Heading'
import { Label } from 'components/v2/Label'
import FormLayout from 'components/v2/FormLayout'

import { verticalMessages } from 'features/Companies/components/Vertical/messages'

import { safeFormatMessage } from 'utils/intl'
import { GTBusinessClientVertical, GTCompaniesV2Autocomplete, GTCompanyV2Vertical } from 'gql/types.generated'
import { newBusinessClientSchema } from './schema'
import {
  INewBusinessClientFormProps,
  INewBusinessClientProps,
  INewBusinessClientFormValues
} from 'features/BusinessClients/types'
import { MultiCompanyAutocomplete } from 'features/Companies/components/MultiCompanyAutocomplete'
import { FilePreview, InputFile } from 'components/v2/InputFile'
import { InputTags } from 'components/v2/InputTags'
import { Checkbox } from 'components/v2/Checkbox'
import { CancelIcon } from 'components/Icons/CancelIcon'
import { useLogin } from 'features/Login/hooks'
import { isEmpty, isObject, isString, uniq } from 'lodash'
import { getUserLabel } from 'features/Users/utils'
import { UserAutocomplete } from 'features/Users/components/UserAutocomplete'
import { Switch } from 'components/v2/Switch'
import { canSelectCompany } from 'features/Login/utils'
import { AddCircleIcon } from 'components/Icons'
import KreditsCategoryForm from 'features/Kredits/components/KreditsCategoryForm'
import { DEFAULT_KREDITS_SETTINGS_VALUES, getPhysicalSimCSVErrors, hasCSVError } from 'features/BusinessClients/utils'
import { PhysicalSimsFormField } from 'features/BusinessClients/components/PhysicalSimsFormField'
import { ApolloError } from '@apollo/client'
import { CreditsFormField } from 'features/BusinessClients/components/CreditsFormField'

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const VERTICALS_LIST = [
  GTBusinessClientVertical.City,
  GTBusinessClientVertical.Team,
  GTBusinessClientVertical.Rental,
  GTBusinessClientVertical.Mobile
]

const messages = defineMessages({
  selectVerticalPlaceholder: {
    id: 'companies.new.form.selectVerticalFirst.placeholder',
    defaultMessage: 'Select a company vertical'
  },
  companyPlaceholder: {
    id: 'companies.new.form.company.placeholder',
    defaultMessage: 'Enter companies'
  },
  companyLabel: {
    id: 'companies.new.form.company.label',
    defaultMessage: 'Company'
  },
  clientNamePlaceholder: {
    id: 'companies.new.form.clientName.placeholder',
    defaultMessage: 'Enter client name'
  },
  kamPlaceholder: {
    id: 'companies.new.form.kam.placeholder',
    defaultMessage: 'Enter KAM'
  },
  cityPlaceholder: {
    id: 'companies.new.form.address.city.placeholder',
    defaultMessage: 'Enter City'
  },
  phonePlaceholder: {
    id: 'companies.new.form.phone.placeholder',
    defaultMessage: 'Enter phone'
  },
  secondaryPhonePlaceholder: {
    id: 'companies.new.form.secondaryPhone.placeholder',
    defaultMessage: 'Enter secondary phone'
  },
  emailDomainsPlaceholder: {
    id: 'companies.new.form.emailDomains.placeholder',
    defaultMessage: 'Enter email domains'
  },
  emailPlaceholder: {
    id: 'companies.new.form.email.placeholder',
    defaultMessage: 'Enter email'
  },
  secondaryEmailPlaceholder: {
    id: 'companies.new.form.secondaryEmail.placeholder',
    defaultMessage: 'Enter secondary email'
  },
  contactNamePlaceholder: {
    id: 'companies.new.form.contactName.placeholder',
    defaultMessage: 'Enter contact name'
  },
  sitesPlaceholder: {
    id: 'companies.new.form.sites.placeholder',
    defaultMessage: 'Select sites'
  }
})

const CheckboxWrapper = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: space-between;

  input {
    margin-left: 0;
  }
`

const NewBusinessClientForm = ({
  error,
  loading,
  isEdit,
  canEditCompany
}: INewBusinessClientFormProps): ReactElement => {
  const intl = useIntl()

  const {
    values,
    touched,
    errors,
    handleChange,
    setFieldValue,
    handleBlur,
    dirty: isDirty
  } = useFormikContext<INewBusinessClientFormValues>()
  const handleFileChange = async (file: File | ''): Promise<void> => {
    await setFieldValue('logoFile', file)
    if (file) {
      setFieldValue('logo', {
        name: file.name,
        path: ''
      })
    } else {
      setFieldValue('logo', {
        name: '',
        path: ''
      })
    }
  }

  const handleVerticalChange = (value: React.ChangeEvent<HTMLInputElement>): void => {
    const vertical = value.target.id as GTBusinessClientVertical
    const verticals = values.verticals.includes(vertical)
      ? values.verticals.filter((v: GTBusinessClientVertical): boolean => v !== vertical)
      : [...values.verticals, vertical]
    setFieldValue('verticals', verticals)

    if (!isEmpty(values.companies)) {
      const companies = values.companies.filter((company): boolean => verticals.includes(company.vertical))
      setFieldValue('companies', companies)
    }
  }
  const verticalIsSelected = values.verticals.length > 0

  const filePreview = isEdit ? values.logo : values.logoFile
  const isShowingFilePreview =
    (isObject(filePreview) && !isEmpty(filePreview.name)) || (isString(filePreview) && !isEmpty(filePreview))
  const companiesVerticals = uniq(
    values.companies.map((company) => company.vertical)
  ) as unknown as GTCompanyV2Vertical[]

  return (
    <>
      <FormLayout.Card>
        <FormLayout.CardHeader>
          <Heading.H4>
            <FormattedMessage id="companyClient.form.client" defaultMessage="Business Client" />
          </Heading.H4>
        </FormLayout.CardHeader>
        <Row noMargin>
          <Column width="50%">
            <Label>
              <FormattedMessage id="companyClient.vertical.label" defaultMessage="Vertical" />
            </Label>
            <CheckboxWrapper>
              {VERTICALS_LIST.map((vertical): ReactElement => {
                return (
                  <Checkbox
                    id={vertical}
                    key={vertical}
                    name="verticals"
                    checked={values.verticals.includes(vertical)}
                    onChange={handleVerticalChange}
                    disabled={!canEditCompany}
                  >
                    {safeFormatMessage({
                      intl,
                      key: vertical,
                      messages: verticalMessages
                    })}
                  </Checkbox>
                )
              })}
            </CheckboxWrapper>
          </Column>
          <Column width="50%">
            <MultiCompanyAutocomplete
              id="companies"
              values={values.companies as unknown as GTCompaniesV2Autocomplete[]}
              onChange={(values): void => {
                setFieldValue('companies', values)
              }}
              onBlur={handleBlur}
              placeholder={
                !verticalIsSelected
                  ? intl.formatMessage(messages.selectVerticalPlaceholder)
                  : intl.formatMessage(messages.companyPlaceholder)
              }
              label={intl.formatMessage(messages.companyLabel)}
              verticals={values.verticals as unknown as GTCompanyV2Vertical[]}
              touched={touched.companies}
              errors={errors.companies}
              disabled={!canEditCompany || !verticalIsSelected}
              filterFn={(companies): GTCompaniesV2Autocomplete[] => {
                // @ts-expect-error FIXME
                return companies.filter(
                  (company) => !companiesVerticals.includes(company.vertical as GTCompanyV2Vertical)
                )
              }}
            />
          </Column>
        </Row>
        <Row noMargin>
          <Column width="50%">
            <Input
              id="clientName"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.clientName}
              placeholder={intl.formatMessage(messages.clientNamePlaceholder)}
              touched={touched.clientName}
              errors={errors.clientName}
              label={<FormattedMessage id="companyClients.new.form.clientName.label" defaultMessage="Client Name" />}
            />
          </Column>
          <Column width="50%">
            <Row>
              <Column>
                <InputFile
                  label={<FormattedMessage id="companyClients.new.form.logo.label" defaultMessage="Logo" />}
                  accept={{
                    'image/jpeg': [],
                    'image/jpg': [],
                    'image/png': []
                  }}
                  onChange={handleFileChange}
                  touched={touched.logoFile}
                  errors={errors.logoFile}
                  optional
                />
              </Column>
            </Row>
            {isShowingFilePreview && (
              <Row>
                <Column>
                  <FilePreview
                    file={filePreview as File}
                    onRemoveFile={(): void => {
                      handleFileChange('')
                    }}
                  />
                </Column>
              </Row>
            )}
          </Column>
        </Row>
        <Row>
          <Column>
            <InputTags
              id="emailDomains"
              values={values.emailDomains}
              onChange={(values: string[]): void => {
                setFieldValue('emailDomains', values)
              }}
              placeholder={intl.formatMessage(messages.emailDomainsPlaceholder)}
              onBlur={handleBlur}
              label={
                <FormattedMessage id="companyClients.new.form.emailDomains.label" defaultMessage="Email Domains" />
              }
              optional
            />
          </Column>
          <Column>
            <Label>
              <FormattedMessage
                id="companyClients.new.form.shareCustomersData.label"
                defaultMessage="Share Customers Data"
              />
            </Label>
            <Switch
              value={values.shareCustomersData}
              onChange={(value: boolean): void => {
                setFieldValue('shareCustomersData', value)
              }}
            />
          </Column>
        </Row>
      </FormLayout.Card>
      <FormLayout.Card>
        <FieldArray
          name="clientContacts"
          render={(arrayHelpers): ReactElement => (
            <>
              <FormLayout.CardHeader>
                <Heading.H4>
                  <FormattedMessage id="companyClients.new.contact" defaultMessage="Contact" />
                </Heading.H4>
                <Button
                  kind="secondary"
                  type="button"
                  onClick={(): void =>
                    arrayHelpers.push({
                      name: '',
                      phone: '',
                      secondaryPhone: '',
                      email: '',
                      secondaryEmail: ''
                    })
                  }
                >
                  <FormattedMessage id="companyClients.new.form.addContact" defaultMessage="Add contact" />
                </Button>
              </FormLayout.CardHeader>
              {values.clientContacts.map((contact, index) => (
                <Fragment key={index}>
                  {index > 0 ? (
                    <FormLayout.CardHeader>
                      <Heading.H4>
                        <FormattedMessage
                          id="businessClient.new.form.nextContact.title"
                          defaultMessage="Contact {idx}"
                          values={{
                            idx: index + 1
                          }}
                        />
                      </Heading.H4>
                      <FormLayout.DeleteItemAction onClick={(): void => arrayHelpers.remove(index)}>
                        <CancelIcon />
                      </FormLayout.DeleteItemAction>
                    </FormLayout.CardHeader>
                  ) : null}

                  <Row>
                    <Column width="50%">
                      <Input
                        id={`clientContacts.${index}.name`}
                        value={contact.name}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        placeholder={intl.formatMessage(messages.contactNamePlaceholder)}
                        touched={getIn(touched, `clientContacts.${index}.name`)}
                        errors={getIn(errors, `clientContacts.${index}.name`)}
                        label={<FormattedMessage id="companyClient.contactName.label" defaultMessage="CONTACT NAME" />}
                      />
                    </Column>
                    <Column>
                      <Input
                        id={`clientContacts.${index}.phone`}
                        type="tel"
                        value={contact.phone}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        placeholder={intl.formatMessage(messages.phonePlaceholder)}
                        touched={getIn(touched, `clientContacts.${index}.phone`)}
                        errors={getIn(errors, `clientContacts.${index}.phone`)}
                        label={<FormattedMessage id="companyClient.phone.label" defaultMessage="Phone" />}
                      />
                    </Column>
                    <Column>
                      <Input
                        id={`clientContacts.${index}.secondaryPhone`}
                        type="tel"
                        value={contact.secondaryPhone}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        placeholder={intl.formatMessage(messages.secondaryPhonePlaceholder)}
                        touched={getIn(touched, `clientContacts.${index}.secondaryPhone`)}
                        errors={getIn(errors, `clientContacts.${index}.secondaryPhone`)}
                        label={
                          <FormattedMessage id="companyClient.secondaryPhone.label" defaultMessage="Secondary Phone" />
                        }
                        optional
                      />
                    </Column>
                  </Row>
                  <Row>
                    <Column width="50%">
                      <Input
                        id={`clientContacts.${index}.email`}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={contact.email}
                        placeholder={intl.formatMessage(messages.emailPlaceholder)}
                        touched={getIn(touched, `clientContacts.${index}.email`)}
                        errors={getIn(errors, `clientContacts.${index}.email`)}
                        label={<FormattedMessage id="companyClient.email.label" defaultMessage="E MAIL" />}
                      />
                    </Column>
                    <Column width="50%">
                      <Input
                        id={`clientContacts.${index}.secondaryEmail`}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={contact.secondaryEmail}
                        placeholder={intl.formatMessage(messages.secondaryEmailPlaceholder)}
                        touched={getIn(touched, `clientContacts.${index}.secondaryEmail`)}
                        errors={getIn(errors, `clientContacts.${index}.secondaryEmail`)}
                        label={
                          <FormattedMessage id="companyClient.secondaryEmail.label" defaultMessage="SECONDARY EMAIL" />
                        }
                        optional
                      />
                    </Column>
                  </Row>
                </Fragment>
              ))}
            </>
          )}
        ></FieldArray>
      </FormLayout.Card>
      <FormLayout.Card>
        <FormLayout.CardHeader>
          <Heading.H4>
            <FormattedMessage id="newBusinessClientForm.kam.title" defaultMessage="KAM" />
          </Heading.H4>
        </FormLayout.CardHeader>
        <Row>
          <Column width="50%">
            <UserAutocomplete
              label={<FormattedMessage id="newBusinessClientForm.kam.label" defaultMessage="KAM Optional" />}
              placeholder={intl.formatMessage(messages.kamPlaceholder)}
              value={values.kam ? getUserLabel(values.kam) : ''}
              onChange={(kam): void => {
                setFieldValue('kam', kam)
              }}
              getItemLabel={getUserLabel}
              touched={touched.kam && touched.kam}
              errors={errors.kam && errors.kam}
              optional
            />
          </Column>
        </Row>
      </FormLayout.Card>
      <PhysicalSimsFormField fieldName="physicalSims" />
      <CreditsFormField fieldName="credits" />

      <FieldArray
        name="kreditsSettings.categories"
        render={(arrayHelpers): ReactElement => {
          return (
            <FormLayout.Card>
              <FormLayout.CardHeader>
                <Heading.H4>
                  <FormattedMessage
                    id="businessClient.form.kreditSettings"
                    defaultMessage="Client Kredit Settings <i>Optional</i>"
                    values={{
                      i: (chunks) => <i>{chunks}</i>
                    }}
                  />
                </Heading.H4>
                <Actions>
                  <IconButton
                    icon={<AddCircleIcon />}
                    onClick={(): void => {
                      arrayHelpers.push(DEFAULT_KREDITS_SETTINGS_VALUES.categories[0])
                    }}
                    disabled={false}
                  />
                </Actions>
              </FormLayout.CardHeader>
              <KreditsCategoryForm fieldName={`kreditsSettings.categories`} />
            </FormLayout.Card>
          )
        }}
      />
      {hasCSVError(error) && <ErrorMessage>{getPhysicalSimCSVErrors(error as ApolloError, intl)}</ErrorMessage>}
      {!hasCSVError(error) && error && (
        <FormLayout.Actions>
          <ErrorMessage>
            <FormattedMessage
              id="companyClient.new.form.error"
              defaultMessage="There was an error while creating your client"
            />
          </ErrorMessage>
        </FormLayout.Actions>
      )}

      <FormLayout.Actions>
        <Button as={NavLink} to="/business-clients" kind="cancel">
          <FormattedMessage id="companyClient.new.form.cancel" defaultMessage="Cancel" />
        </Button>
        <Button type="submit" kind="primary" loading={loading} disabled={!isDirty}>
          {isEdit ? (
            <FormattedMessage id="dictionary.update" defaultMessage="Update" />
          ) : (
            <FormattedMessage id="dictionary.create" defaultMessage="Create" />
          )}
        </Button>
      </FormLayout.Actions>
    </>
  )
}

export const NewBusinessClient = ({
  businessClient,
  onSubmit,
  error,
  loading,
  isEdit,
  businessClientId
}: INewBusinessClientProps): ReactElement => {
  const login = useLogin()
  const intl = useIntl()
  const initialClientFormValues: INewBusinessClientFormValues = {
    companies: businessClient?.companies || [],
    clientContacts: businessClient?.clientContacts || [],
    clientName: businessClient?.clientName || '',
    logo: businessClient?.logo || {
      name: '',
      path: ''
    },
    logoFile: '',
    emailDomains: businessClient?.emailDomains || [],
    shareCustomersData: businessClient?.shareCustomersData || true,
    kam: businessClient?.kam,
    verticals: businessClient?.verticals || [],
    kreditsSettings: businessClient?.kreditsSettings || DEFAULT_KREDITS_SETTINGS_VALUES,
    physicalSims: businessClient?.physicalSims || [],
    credits: businessClient?.credits || []
  }

  return (
    <Formik
      initialValues={initialClientFormValues}
      validationSchema={newBusinessClientSchema(intl, initialClientFormValues)}
      onSubmit={onSubmit}
    >
      {(props): ReactElement => {
        return (
          <FormLayout.Content as={'form'} onSubmit={props.handleSubmit}>
            <NewBusinessClientForm
              {...props}
              businessClientId={businessClientId}
              error={error}
              loading={loading}
              isEdit={isEdit}
              canEditCompany={canSelectCompany(login.accessToken)}
            />
          </FormLayout.Content>
        )
      }}
    </Formik>
  )
}
